import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/auth/authContext';
import { useLearningLab } from '../../contexts/LearningLabContext';
import { hasRole } from '../../utils/roleHelpers';
import { detectSubdomain } from '../../utils/subdomainUtils';
import {
  getLearningLabNavAriaLabel,
  getLearningLabNavStatusMessage,
  getLearningLabNavTooltip,
  isLearningLabNavIconActive,
  isLearningLabNavIconDisabled,
  isLearningLabNavIconVisible,
  resolveLearningLabNavState,
  shouldShowLearningLabNav,
  type LearningLabNavContext,
  type LearningLabNavState,
} from './learningLabNavVisibility';

export function useLearningLabNavContext(): LearningLabNavContext {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();
  const subdomainInfo = useMemo(() => detectSubdomain(), []);
  const {
    featureEnabled,
    serverEnabled,
    provisioned,
    active,
    isStale,
    loading,
    statusUnavailable,
  } = useLearningLab();

  return useMemo(
    () => ({
      isAuthenticated,
      isCoordinator: hasRole(user, 'coordinator'),
      featureEnabled,
      serverEnabled,
      provisioned,
      active,
      isStale,
      loading,
      statusUnavailable,
      isImpersonating: user?.is_impersonation === true,
      pathname: location.pathname,
      subdomain: subdomainInfo.subdomain ?? '',
    }),
    [
      active,
      featureEnabled,
      isAuthenticated,
      isStale,
      loading,
      location.pathname,
      provisioned,
      serverEnabled,
      statusUnavailable,
      subdomainInfo.subdomain,
      user,
    ],
  );
}

export function useLearningLabNav(): {
  navContext: LearningLabNavContext;
  navState: LearningLabNavState;
  showNav: boolean;
  iconVisible: boolean;
  iconDisabled: boolean;
  iconActive: boolean;
  ariaLabel: string;
  tooltip: string | null;
  statusMessage: string | null;
  showOnRoute: boolean;
} {
  const navContext = useLearningLabNavContext();
  const navState = useMemo(
    () => resolveLearningLabNavState(navContext),
    [navContext],
  );
  const showOnRoute = shouldShowLearningLabNav(
    navContext.pathname,
    navContext.subdomain,
  );

  return {
    navContext,
    navState,
    showNav: isLearningLabNavIconVisible(navState),
    iconVisible: isLearningLabNavIconVisible(navState),
    iconDisabled: isLearningLabNavIconDisabled(navState),
    iconActive: isLearningLabNavIconActive(navState),
    ariaLabel: getLearningLabNavAriaLabel(navState),
    tooltip: getLearningLabNavTooltip(navState),
    statusMessage: getLearningLabNavStatusMessage(navState),
    showOnRoute,
  };
}
